import { animated, useSpring } from '@react-spring/web';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Input } from '../../../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../../../web/components-v2/Select';
import { ISelectOption, MultiSelect } from '../../../../../../web/components/DeprecatedSelect';
import { Toggle } from '../../../../../../web/components/Toggle';
import { AccountDetailsInfoField } from '../../../../../components/accountDetails/AccountDetailsInfoField';
import { styleSheet } from '../styles';

interface ISetting<T> {
	category: SettingCategory;
	info?: string;
	/** Rendered after the input for SettingType.Input rows (e.g. a saving indicator). */
	inputAccessory?: React.ReactNode;
	inputProps?: Partial<React.ComponentProps<typeof Input>>;
	label?: string | React.ReactNode;
	onSelectionChange?: (selectedOption: ISelectOption<T>) => void;
	onMultiSelectionChange?: (selectedOptions: ISelectOption<T>[]) => void;
	onToggle?: (checked: boolean) => void;
	options?: ISelectOption<T>[];
	selectedOption?: ISelectOption<T>;
	selectedOptions?: ISelectOption<T>[];
	settingDisabled?: boolean;
	type: SettingType;
	value?: boolean | (() => boolean);
}

interface IProps<T> {
	canModifyAccount?: boolean;
	disabled?: boolean;
	data: ISetting<T>;
	customText?: string;
	/** The expand/collapse spring animates to a fixed height; override for rows taller than the 56px default. */
	expandedHeight?: number;
	styles?: StyleDeclarationValue[];
	onCustomCallback?(): void;
	isNew?: boolean;
}

export enum SettingCategory {
	Administrative = 'Administrative',
	Dashboard = 'Dashboard',
	EmailTracking = 'Email Tracking',
	Features = 'Features',
	PreApprovedContent = 'Pre Approved Content',
	Permanent = 'Permanent',
	Databoards = 'Databoards',
}

export enum SettingType {
	Dropdown = 'Dropdown',
	Custom = 'Custom',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Input = 'Input',
	Multi = 'Multi',
	MultiWInput = 'MultiWInput',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	MultiSelect = 'MultiSelect',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Toggle = 'Toggle',
}

export function Setting<T>({
	canModifyAccount,
	data,
	disabled,
	customText,
	expandedHeight = 56,
	onCustomCallback,
	isNew,
	styles,
}: IProps<T>) {
	const id = useRef(uuid()).current;
	const hasRendered = useRef(true);
	const spring = useSpring({
		delay: 100,
		from: { height: !disabled ? 0 : expandedHeight, opacity: !disabled ? 0 : 1 },
		immediate: hasRendered.current,
		to: { height: !disabled ? expandedHeight : 0, opacity: !disabled ? 1 : 0 },
	});

	useEffect(() => {
		hasRendered.current = false;
	}, []);

	const computedStyles = [
		!canModifyAccount || disabled ? styleSheet.disable : null,
		styleSheet.infoFieldContainerBottom,
		data.category === SettingCategory.EmailTracking || data.category === SettingCategory.Administrative
			? styleSheet.infoFieldContainerLeft
			: null,
		data.category === SettingCategory.Permanent ? styleSheet.infoFieldContainerFullContainer : null,
		...(styles || []),
	];

	const hasBooleanValue = data.value !== null && data.value !== undefined;
	const booleanValue = hasBooleanValue ? (typeof data.value === 'function' ? data.value() : data.value) : false;

	return (
		<animated.div className={css(styleSheet.expandable)} style={spring}>
			<AccountDetailsInfoField
				disabled={disabled || data.settingDisabled}
				label={data.label}
				info={data.info}
				isNew={isNew}
				key={id}
				labelStyles={[styleSheet.infoFieldValueBottom]}
				styles={computedStyles}
				valueStyles={[styleSheet.infoFieldValueBottom]}
			>
				{data.type === SettingType.Dropdown && (
					<Select
						value={data.selectedOption?.id ?? ''}
						onValueChange={value => {
							const option = data.options?.find(o => o.id === value);
							if (option) data.onSelectionChange?.(option);
						}}
					>
						<SelectTrigger
							disabled={disabled || !canModifyAccount || data.settingDisabled}
							styles={styleSheet.dropdown}
						>
							<span>{data.selectedOption?.text ?? ''}</span>
						</SelectTrigger>
						<SelectContent>
							{(data.options || []).map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				{data.type === SettingType.MultiSelect && (
					<MultiSelect
						disabled={disabled || !canModifyAccount || data.settingDisabled}
						onOptionClick={(option, wasSelected) => {
							if (data.onMultiSelectionChange) {
								const currentSelectedOptions = data.selectedOptions || [];
								const newSelectedOptions = wasSelected
									? [...currentSelectedOptions, option]
									: currentSelectedOptions.filter(o => o.id !== option.id);
								data.onMultiSelectionChange(newSelectedOptions);
							}
						}}
						options={data.options || []}
						selectedOptions={data.selectedOptions || []}
						selectedOptionsTitle={
							data.selectedOptions && data.selectedOptions.length > 0
								? `${data.selectedOptions.length} selected`
								: 'Select options...'
						}
						styles={[styleSheet.dropdown]}
					/>
				)}
				{data.type === SettingType.Toggle && (
					<Toggle
						disabled={disabled || !canModifyAccount || data.settingDisabled}
						id={id}
						isOn={!!booleanValue}
						onToggleCheckChanged={data?.onToggle}
						uncheckedColor='#ccc'
					/>
				)}
				{data.type === SettingType.Input ? (
					<>
						<Input
							{...(data?.inputProps || {})}
							disabled={data?.inputProps?.disabled || disabled || !canModifyAccount || data.settingDisabled}
							id={id}
							type={data?.type || 'text'}
						/>
						{data.inputAccessory}
					</>
				) : null}
				{data.type === SettingType.Custom && (
					<button
						className={!disabled ? css(styleSheet.customRow) : ''}
						onClick={disabled || !canModifyAccount ? null : onCustomCallback}
					>
						{customText}
					</button>
				)}
				{data.type === SettingType.Multi && (
					<>
						{booleanValue && (
							<Select
								value={data.selectedOption?.id ?? ''}
								onValueChange={value => {
									const option = data.options?.find(o => o.id === value);
									if (option) data.onSelectionChange?.(option);
								}}
							>
								<SelectTrigger
									disabled={disabled || !canModifyAccount || data.settingDisabled}
									styles={styleSheet.dropdown}
									className={css(styleSheet.multiText)}
								>
									<span>{data.selectedOption?.text ?? ''}</span>
								</SelectTrigger>
								<SelectContent>
									{(data.options || []).map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}

						<Toggle
							disabled={disabled || !canModifyAccount || data.settingDisabled}
							id={id}
							isOn={!!booleanValue}
							onToggleCheckChanged={data?.onToggle}
							uncheckedColor='#ccc'
						/>
					</>
				)}
				{data.type === SettingType.MultiWInput && (
					<>
						{booleanValue && (
							<>
								<Input
									{...(data?.inputProps || {})}
									disabled={data?.inputProps?.disabled || disabled || !canModifyAccount || data.settingDisabled}
									id={id}
									type={data?.type || 'text'}
									style={{ width: 100 }}
								/>
								&nbsp;&nbsp;&nbsp;
								<Select
									value={data.selectedOption?.id ?? ''}
									onValueChange={value => {
										const option = data.options?.find(o => o.id === value);
										if (option) data.onSelectionChange?.(option);
									}}
								>
									<SelectTrigger
										disabled={disabled || !canModifyAccount || data.settingDisabled}
										styles={styleSheet.dropdown}
										className={css(styleSheet.multiText)}
									>
										<span>{data.selectedOption?.text ?? ''}</span>
									</SelectTrigger>
									<SelectContent>
										{(data.options || []).map(option => (
											<SelectItem key={option.id} value={option.id}>
												{option.text}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</>
						)}

						<Toggle
							disabled={disabled || !canModifyAccount || data.settingDisabled}
							id={id}
							isOn={!!booleanValue}
							onToggleCheckChanged={data?.onToggle}
							uncheckedColor='#ccc'
						/>
					</>
				)}
			</AccountDetailsInfoField>
		</animated.div>
	);
}
